import { convertLetters } from "@/utils/letterConversion";
import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
} from "@headlessui/react";
import {
    ExclamationTriangleIcon,
    InformationCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";

export interface VillageModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm?: () => void;
    title: string;
    message: string;
    type?: "alert" | "confirm" | "error" | "success" | "info";
    confirmText?: string;
    cancelText?: string;
}

/**
 * VillageModal - A branded modal component for the Village UI
 * Replaces native alert() and confirm() dialogs with Viking-themed styling
 *
 * Usage:
 * - Alert mode: Set type="alert", no onConfirm prop
 * - Confirm mode: Set type="confirm", provide onConfirm callback
 * - Error/Success/Info: Use appropriate type for icon display
 */
export default function VillageModal({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    type = "info",
    confirmText = "Confirm",
    cancelText = "Cancel",
}: VillageModalProps) {
    const isConfirmMode = type === "confirm" && onConfirm !== undefined;

    const handleConfirm = () => {
        if (onConfirm) {
            onConfirm();
        }
        onClose();
    };

    const getIcon = () => {
        switch (type) {
            case "error":
                return (
                    <ExclamationTriangleIcon className="h-8 w-8 text-red-400" />
                );
            case "success":
                return (
                    <InformationCircleIcon className="h-8 w-8 text-green-400" />
                );
            case "confirm":
                return (
                    <ExclamationTriangleIcon className="h-8 w-8 text-yellow-400" />
                );
            default:
                return <InformationCircleIcon className="text-brand h-8 w-8" />;
        }
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-50">
            <DialogBackdrop
                transition
                className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
            />

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        transition
                        className="relative transform overflow-hidden rounded-lg border-2 border-slate-700 bg-slate-900/95 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-lg data-closed:sm:translate-y-0 data-closed:sm:scale-95"
                    >
                        <div className="bg-slate-900/50 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="sm:flex sm:items-start">
                                <div className="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-slate-800/80 sm:mx-0 sm:h-10 sm:w-10">
                                    {getIcon()}
                                </div>
                                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <DialogTitle
                                        as="h3"
                                        className="font-prviking text-brand text-xl leading-6"
                                    >
                                        {convertLetters(title)}
                                    </DialogTitle>
                                    <div className="mt-2">
                                        <p className="font-garamond text-base text-slate-300">
                                            {message}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="focus:ring-brand absolute top-4 right-4 rounded-md text-slate-400 hover:text-slate-300 focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:outline-none"
                                >
                                    <span className="sr-only">Close</span>
                                    <XMarkIcon
                                        className="h-6 w-6"
                                        aria-hidden="true"
                                    />
                                </button>
                            </div>
                        </div>
                        <div className="gap-3 bg-slate-900/50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            {isConfirmMode ? (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleConfirm}
                                        className="border-brand bg-brand font-prviking hover:bg-brand/90 focus-visible:outline-brand inline-flex w-full justify-center rounded-md border-2 px-4 py-2 text-base font-semibold text-slate-900 shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 sm:w-auto"
                                    >
                                        {convertLetters(confirmText)}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        className="font-prviking mt-3 inline-flex w-full justify-center rounded-md border-2 border-slate-700 bg-slate-800/80 px-4 py-2 text-base font-semibold text-slate-300 shadow-sm hover:bg-slate-700/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700 sm:mt-0 sm:w-auto"
                                    >
                                        {convertLetters(cancelText)}
                                    </button>
                                </>
                            ) : (
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="border-brand bg-brand font-prviking hover:bg-brand/90 focus-visible:outline-brand inline-flex w-full justify-center rounded-md border-2 px-4 py-2 text-base font-semibold text-slate-900 shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 sm:w-auto"
                                >
                                    {convertLetters("OK")}
                                </button>
                            )}
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}
